import React, { useMemo } from "react";
import DetailedView, { TabOption } from "@/components/layouts/DetailedView";
import { useParams } from "react-router-dom";
import { usePartners } from "@/pages/partners/hooks";
import PartnerTopFields from "@/pages/partners/components/PartnerTopFields";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { ComputedFields } from "@/services/api/types";
import { usePartnerContactsInfinite } from "@/pages/partners/contacts/hooks";
import { PartnerContactColumn } from "@/pages/partners/contacts/types";
import {
  Activity,
  Dashboard,
  Diamond,
  Magnet,
  People
} from "@dreamhub/designsystem/icons";
import { useDropdownClosing } from "@/services/hooks/useDropdownClosing";
import { usePartnerActions } from "@/services/hooks/entities/partner/usePartnerActions";
import OptionsDropdownMenu from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { UserData } from "@/entities/users/types";
import { EntityData } from "@/services/api/types";
import PartnerOverviewContent from "./PartnerOverviewContent";

interface Props {
  userData: UserData;
}

const PartnerDetailedView: React.FC<Props> = ({ userData }) => {
  const { partnerId } = useParams<{ partnerId: string }>();

  const { isDropdownClosing } = useDropdownClosing();

  const {
    entities: [partnerEntity],
    isError: isPartnerError
  } = usePartners([partnerId || ""], {
    forceRefetch: true,
    computed_fields: ComputedFields.ALL
  });

  const {
    partner,
    isTasksDrawerOpen,
    toggleTasksDrawer,
    onUpdateField,
    getCreateOptions,
    getOptions,
    renderModals
  } = usePartnerActions(partnerEntity, true);

  const { total: peopleTotal } = usePartnerContactsInfinite(
    { [PartnerContactColumn.PARTNER]: [partnerId || ""] },
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    false,
    !!partnerId,
    ComputedFields.MINIMAL
  );

  const tabIcons = useMemo(
    () => ({
      [TabOption.OVERVIEW]: <Dashboard />,
      [TabOption.LEADS]: <Magnet />,
      [TabOption.DEALS]: <Diamond />,
      [TabOption.ACTIVITIES]: <Activity />,
      [TabOption.PEOPLE]: <People />
    }),
    []
  );

  const tabBadges = useMemo(
    () => ({
      [TabOption.LEADS]: partner?.openLeadsCount ?? 0,
      [TabOption.DEALS]: partner?.openDealsCount ?? 0,
      [TabOption.PEOPLE]: peopleTotal ?? 0
    }),
    [partner?.openLeadsCount, partner?.openDealsCount, peopleTotal]
  );

  return (
    <>
      <DetailedView
        entityData={partner as EntityData}
        entityType={EntityType.PARTNER}
        isError={isPartnerError}
        options={getOptions()}
        topRightComponent={
          <div
            data-test="partner-detailed-view-actions"
            className="flex items-center gap-2"
          >
            <OptionsDropdownMenu
              options={getCreateOptions()}
              tPrefix="detailedView.create"
              triggerIcon="add"
              iconSize="lg"
              triggerDataTest="partner-detailed-view-create-entity-button"
            />
          </div>
        }
        onUpdateField={onUpdateField}
        isTasksDrawerOpen={isTasksDrawerOpen}
        toggleTasksDrawer={toggleTasksDrawer}
        tabIcons={tabIcons}
        tabBadges={tabBadges}
        topFields={
          <PartnerTopFields
            data-test="partner-detailed-view-top-fields"
            partner={partner}
            onUpdateField={onUpdateField}
            className="min-h-[2.7rem]"
          />
        }
        overviewContent={
          <PartnerOverviewContent
            data-test="partner-detailed-view-overview"
            partner={partner}
            onUpdateField={onUpdateField}
            getCreateOptions={getCreateOptions}
          />
        }
      />
      {renderModals(isDropdownClosing, false, undefined, userData)}
    </>
  );
};

export default PartnerDetailedView;
